
import { Device } from "@/api/dtos/Device.schema";
import { DeviceDimensions } from "@/api/dtos/DeviceDimensions.schema";
import { DeviceTypeSettings } from "@/api/dtos/DeviceTypeSettings.schema";
import { RentalMethod } from "@/api/dtos/RentalMethod.schema";
import { Station } from "@/api/dtos/Station.schema";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getDeviceDummy } from "@/misc/dto_factory";
import EditFrame from "../EditFrame.vue";
import DeviceChooser from "./DeviceChooser.vue";
import DeviceSettingsDetails from "./DeviceSettingsDetails.vue";
import AddDeviceDialog from "../dialogs/AddDeviceDialog.vue";
import { isNumber } from "validate.js";
import { DeviceHardwareSettings } from "@/api/dtos/DeviceHardwareSettings.schema";
import { VestaWts04Address_v0_1 } from "@/api/dtos/VestaWts04Address_v0_1.schema";
import { VestaWts04OutAddress_v0_1 } from "@/api/dtos/VestaWts04OutAddress_v0_1.schema";

@Component({
  components: {
    DeviceChooser,
    EditFrame,
    DeviceSettingsDetails,
    AddDeviceDialog,
  },
})
export default class DeviceSettings extends Vue {
  @Prop()
  station!: Station;

  @Prop({
    default: true,
  })
  includeOverviewTab!: boolean;

  @Prop({
    default: false,
  })
  creationMode!: boolean;

  selectedDeviceNumber = -1;
  showAddDeviceDialog = false;
  showDeviceModal = false;

  selectedDevice: Device | null = null;

  resetDeviceEditState() {
    const deviceSettings = this.$refs.deviceSettingsDetails as any;
    if (deviceSettings) {
      deviceSettings.editMode = false;
      deviceSettings.resetLocalChanges();
    }
  }

  closeDeviceModal() {
    this.resetDeviceEditState();
    this.showDeviceModal = false;
    this.selectedDeviceNumber = -1;
  }

  onClickOutside() {
    this.closeDeviceModal();
  }

  @Watch("selectedDeviceNumber")
  @Watch("station")
  onSelectedDeviceNumberChanged(newVal: number): void {
    this.selectedDevice =
      this.station.devices.find(
        (device) => device.deviceNumber === this.selectedDeviceNumber
      ) ?? null;

    if (isNumber(newVal) && this.selectedDevice === null && newVal !== -1) {
      this.showAddDeviceDialog = true;
    } else if (this.selectedDevice !== null) {
      this.showDeviceModal = true;
    }
  }

  onDeviceHardwareSettingsSave(o: {
    deviceNumber: number;
    deviceHardwareSettings: DeviceHardwareSettings;
  }): void {
    if (!this.selectedDevice) return;
    this.$emit("deviceHardwareSettingsSave", o);
  }

  onDeviceDescriptionSave(o: {
    deviceNumber: number;
    descriptionText: string;
  }): void {
    if (!this.selectedDevice) return;
    this.$emit("deviceDescriptionSave", o);
  }

  onRentalMethodSave(o: {
    deviceNumber: number;
    rentalMethod: RentalMethod;
  }): void {
    if (!this.selectedDevice) return;
    this.selectedDevice.rentalMethod = o.rentalMethod;
    this.$emit("rentalMethodSave", o);
  }

  onRentalTimeSave(o: {
    deviceNumber: number;
    maxPrebookDuration: string;
    maxRentalDuration: string;
    minInterBookingDuration: string;
  }): void {
    if (!this.selectedDevice) return;
    this.$emit("rentalTimeSave", o);
  }

  onDeviceDimensionsSave(o: {
    deviceNumber: number;
    deviceDimensions: DeviceDimensions;
  }): void {
    if (!this.selectedDevice) return;
    this.$emit("deviceDimensionsSave", o);
  }

  onDeviceCapacitySave(o: { deviceNumber: number; capacity: number }): void {
    if (!this.selectedDevice) return;
    this.$emit("deviceCapacitySave", o);
  }

  onDevicePropertiesSave(o: {
    deviceNumber: number;
    deviceTypeSettings: DeviceTypeSettings;
  }): void {
    if (!this.selectedDevice) return;
    this.$emit("devicePropertiesSave", o);
  }

  onNewDeviceConfirmed(deviceTypeToCreate: string): void {
    const newDevice = getDeviceDummy(deviceTypeToCreate);
    newDevice.deviceNumber = this.selectedDeviceNumber;
    newDevice.hardwareSettings.wts04Out.wts04 = this.deviceNumberToWtsAddress(newDevice.deviceNumber);
    newDevice.hardwareSettings.wts04Out.wts04OutPort = this.deviceNumberToWtsOutPort(newDevice.deviceNumber);
    this.selectedDevice = newDevice;
    this.$emit("deviceCreated", newDevice);
    this.showAddDeviceDialog = false;
    this.showDeviceModal = true;
  }

  deviceNumberToWtsAddress(deviceNumber: number): VestaWts04Address_v0_1
  {
    const wts = Math.floor((deviceNumber-1) / 4);
    switch (wts) {
      case 0: return "WTS00";
      case 1: return "WTS01";
      case 2: return "WTS02";
      case 3: return "WTS03";
      case 4: return "WTS04";
      case 5: return "WTS05";
      case 6: return "WTS06";
      case 7: return "WTS07";
      case 8: return "WTS08";
      case 9: return "WTS09";
      case 10: return "WTS10";
      case 11: return "WTS11";
      case 12: return "WTS12";
      case 13: return "WTS13";
      case 14: return "WTS14";
      case 15: return "WTS15";
      default: return "WTS00";
    }
  }

  deviceNumberToWtsOutPort(deviceNumber: number): VestaWts04OutAddress_v0_1
  {
    const port = (deviceNumber-1) % 4;
    switch (port) {
      case 0: return "OUT1";
      case 1: return "OUT2";
      case 2: return "OUT3";
      case 3: return "OUT4";
      default: return "OUT1";
    }
  }

  onNewDeviceCancelled(): void {
    this.selectedDeviceNumber = -1;
  }

  onDeviceDeleted(deviceNumber: number): void {
    this.selectedDeviceNumber = -1;
    this.closeDeviceModal();
    this.$emit("deviceDeleted", deviceNumber);
    this.closeDeviceModal();
  }
}
